import React, { useCallback } from "react";
import type { FormikConfig, FormikHelpers } from "formik";
import { Field, Form as FormBase, Formik as FormikBase } from "formik";
import { Alert, Box, Button, DialogActions, Grid } from "@mui/material";

import RpMUIDialog from "@/components/modals/RpMUIModal";
import RpFieldLabel from "@/components/muiCustom/label/RpFieldLabel";
import RpFormikTextField from "@/components/muiCustom/textField/RpFormikTextField";
import PermissionsNoticeBox from "@/components/permissions/PermissionsNoticeBox";
import { shouldBlockUnsafeSubmission } from "@/helpers/sanitizeHelper";

import { USER_MANAGEMENT_PERMISSION_NOTICE } from "../constants/userManagementConstants";
import {
  addSubUserInitialValues,
  addSubUserValidationSchema,
} from "../utils/userManagementValidators";
import UserManagementModalHeader from "./UserManagementModalHeader";

const addSubUserDialogSx = {
  "& .MuiDialogContent-root": {
    border: 0,
    p: 0,
  },
};

const addSubUserInputSx = {
  mt: 1,
  "& .MuiOutlinedInput-root": {
    borderRadius: "var(--radius-control)",
  },
  "& .MuiOutlinedInput-notchedOutline": {
    borderColor: "primary.main",
  },
};

type AddSubUserValues = {
  firstName: string;
  lastName: string;
  email: string;
  phoneNumber: string;
  jobTitle: string;
};

type AddSubUserFieldProps = {
  label: string;
  name: keyof AddSubUserValues;
  required?: boolean;
  gridSize?: number;
  disabled?: boolean;
};

const AddSubUserField = ({
  label,
  name,
  required = false,
  gridSize = 6,
  disabled = false,
}: AddSubUserFieldProps) => (
  <Grid size={{ xs: 12, sm: gridSize }}>
    <RpFieldLabel required={required}>{label}</RpFieldLabel>
    <Field
      name={name}
      component={RpFormikTextField}
      disabled={disabled}
      sx={addSubUserInputSx}
    />
  </Grid>
);

type AddSubUserModalProps = {
  open: boolean;
  onClose: () => void;
  onSubmit?: (_values: AddSubUserValues) => void | Promise<void>;
  showCloseIcon?: boolean;
  initialValues?: AddSubUserValues;
  title?: string;
  submitLabel?: string;
  backendError?: string;
  disableEmail?: boolean;
};

const Formik = FormikBase as unknown as React.ComponentType<
  FormikConfig<AddSubUserValues> & { className?: string }
>;
const Form = FormBase as unknown as React.ComponentType<
  React.FormHTMLAttributes<HTMLFormElement>
>;

const AddSubUserModal = ({
  open,
  onClose,
  onSubmit,
  showCloseIcon = true,
  initialValues = addSubUserInitialValues,
  title = "Add new Sub User",
  submitLabel = "Continue",
  backendError = "",
  disableEmail = false,
}: AddSubUserModalProps) => {
  const handleSubmit = useCallback(
    async (
      values: AddSubUserValues,
      formikActions: FormikHelpers<AddSubUserValues>
    ) => {
      if (shouldBlockUnsafeSubmission(values)) {
        formikActions.setSubmitting(false);
        return;
      }

      try {
        await onSubmit?.({
          firstName: values.firstName.trim(),
          lastName: values.lastName.trim(),
          email: values.email.trim(),
          phoneNumber: values.phoneNumber.trim(),
          jobTitle: values.jobTitle.trim(),
        });
      } finally {
        formikActions.setSubmitting(false);
      }
    },
    [onSubmit]
  );

  return (
    <RpMUIDialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      PaperProps={{ sx: { borderRadius: "var(--radius-container)" } }}
      sx={addSubUserDialogSx}
    >
      <Formik
        enableReinitialize
        initialValues={initialValues}
        validationSchema={addSubUserValidationSchema}
        onSubmit={handleSubmit}
        className="p-2"
      >
        {({ isSubmitting }) => (
          <Form>
            <Box className="px-3 px-sm-4 pt-3">
              <UserManagementModalHeader
                title={title}
                onClose={onClose}
                showCloseIcon={showCloseIcon}
              />

              {backendError ? (
                <Alert severity="error" className="mb-3">
                  {backendError}
                </Alert>
              ) : null}

              <Grid container spacing={2.5}>
                <AddSubUserField label="First Name" name="firstName" required />
                <AddSubUserField label="Last Name" name="lastName" required />
                <AddSubUserField
                  label="Email ID"
                  name="email"
                  required
                  disabled={disableEmail}
                />
                <AddSubUserField label="Phone Number" name="phoneNumber" />
                <AddSubUserField
                  label="Job Title"
                  name="jobTitle"
                  gridSize={12}
                />
                <Grid size={12}>
                  <PermissionsNoticeBox
                    message={USER_MANAGEMENT_PERMISSION_NOTICE.message}
                    permissions={USER_MANAGEMENT_PERMISSION_NOTICE.permissions}
                  />
                </Grid>
              </Grid>
            </Box>

            <DialogActions className="px-3 py-4 px-sm-4 pt-3 pb-4 justify-content-end">
              <Button
                variant="outlined"
                color="primary"
                onClick={onClose}
                sx={{
                  minWidth: { xs: 120, sm: 150 },
                }}
              >
                Cancel
              </Button>
              <Button
                className="text-capitalize"
                type="submit"
                variant="contained"
                color="primary"
                disabled={isSubmitting}
                sx={{
                  minWidth: { xs: 140, sm: 220 },
                }}
              >
                {isSubmitting ? "Saving..." : submitLabel}
              </Button>
            </DialogActions>
          </Form>
        )}
      </Formik>
    </RpMUIDialog>
  );
};

export default AddSubUserModal;
